'use client';
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { FaBars, FaBell, FaTimes } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import { useUser } from '@/context/UserContext';
import ProblemReportOverlay from './ProblemReportOverlay';
import { collectClientContext } from '@/app/utils/clientContext';
import { getUserImage, logoUrl } from '../utils/utils';
import { ReactNode } from 'react';
import { useToast } from '@/context/ToastProvider';
import { ThemeDebug } from './ThemeDebug';
import { useHasSites } from '@/app/lib/useHasSites';


interface IINavBarProps {
    headerContent?: ReactNode;
}

const IINavBar = ({ headerContent }: IINavBarProps) => {
    const toast = useToast();
    const pathname = usePathname()
    const { user, installer, isAnonymous, isHacker, isInstaller, isNone, isActive } = useUser();
    // Rates only makes sense when there's a "site like yours" to compare
    // against — without one the numbers are misleading because system
    // design and install dominate the result. Staff (installer / hacker /
    // admin) always see Rates because they compare across portfolios.
    const { hasSites } = useHasSites();
    const showRates = isHacker || hasSites === true;
    const [installer_name, setInstallerName] = useState(installer?.name || 'Powston');
    const [isProblemReportOpen, setIsProblemReportOpen] = useState(false);
    const [screenshot, setScreenshot] = useState('');
    const [showDebugUI, setShowDebugUI] = useState(false);

    const handleProblemReport = async (description: string, screenshot: string, url: string, selectedArea: string, cc_me: boolean) => {
        try {
          // Capture browser/display/network state so support can rule out
          // client-side issues (dark mode rendering, narrow viewport, slow
          // connection, browser extension interference, etc.) without a
          // back-and-forth.
          const client_context = collectClientContext();
          const response = await fetch('/api/report_problem', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              description,
              screenshot,
              url,
              cc_me,
              selectedArea,
              client_context,
            }),
          });
    
          if (response.ok) {
            toast.showSuccess('Problem reported successfully');
          } else {
            toast.showError('Failed to report problem');
          }
        } catch (error) {
          console.error('Error reporting problem:', error);
          toast.showError('An error occurred while reporting the problem');
        }
      };

    const navigation = () => {
        if (isInstaller && user?.installer_id) {
                return [
                    { name: 'Home', href: '/', current: (pathname === '/') },
                    { name: 'Sites', href: '/sites', current: (pathname === '/sites') },
                    { name: 'Installer Admin', href: `/installer/${user.installer_id}`, current: (pathname === `/installer/${user.installer_id}`) },
                    { name: 'Rates', href: '/rates', current: (pathname === '/rates') },
                    { name: 'Configure', href: '/configure', current: (pathname === '/configure') },
                ];
            }
        if (isHacker) {
            return [
                { name: 'Home', href: '/', current: (pathname === '/') },
                { name: 'Sites', href: '/sites', current: (pathname === '/sites') },
                { name: 'Rates', href: '/rates', current: (pathname === '/rates') },
                { name: 'Configure', href: '/configure', current: (pathname === '/configure') },
            ]};
        if (isNone) {
            return [
                { name: 'Home', href: '/', current: (pathname === '/') },
            ]};
        if (isAnonymous) {
            return [
                { name: 'Login', href: '/login', current: (pathname === '/') },
            ]};
        return [
                { name: 'Home', href: '/', current: (pathname === '/') },
                ...(showRates ? [{ name: 'Rates', href: '/rates', current: (pathname === '/rates') }] : []),
                { name: 'Configure', href: '/configure', current: (pathname === '/configure') },
            ];
    };

    function classNames(...classes: string[]) {
        return classes.filter(Boolean).join(' ');
    };

    const openReportProblem = () => {
        html2canvas(document.body).then(canvas => {
            setScreenshot(canvas.toDataURL());
            setIsProblemReportOpen(true);
        });
        
    };
    return (
        <>
        <Disclosure as="nav" className="w-full bg-background border-b border-border">
            {({ open }) => (
                <>
                    <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
                        <div className="relative flex h-16 items-center justify-between">
                            <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                                {/* Mobile menu button*/}
                                <DisclosureButton className="relative inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-muted hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary">
                                    <span className="absolute -inset-0.5" />
                                    <span className="sr-only">Open main menu</span>
                                    {open ? (
                                        <FaTimes className="block h-6 w-6" aria-hidden="true" />
                                    ) : (
                                        <FaBars className="block h-6 w-6" aria-hidden="true" />
                                    )}
                                </DisclosureButton>
                            </div>
                            <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                                <div className="hidden sm:flex flex-shrink items-center justify-start sm:justify-center">
                                    <div className="bg-background rounded p-1">
                                        <img
                                            className="h-12 w-auto"
                                            src={logoUrl(user?.installer_logo || 'powston-dots.svg')}
                                            alt={installer_name}
                                        />
                                    </div>
                                </div>
                                <div className="hidden sm:ml-6 sm:block">
                                    <div className="flex space-x-4">
                                        {navigation().map((item) => (
                                            <a
                                                key={'navigation=' + item.name}
                                                href={item.href}
                                                className={classNames(
                                                    item.current 
                                                        ? 'bg-primary text-primary-foreground' 
                                                        : 'text-foreground hover:bg-muted hover:text-accent-foreground',
                                                    'rounded-md px-3 py-2 text-sm font-medium transition-colors'
                                                )}
                                                aria-current={item.current ? 'page' : undefined}
                                            >
                                                {item.name}
                                            </a>
                                        ))}
                                        <button
                                            onClick={() => openReportProblem()}
                                            className="text-foreground hover:bg-muted hover:text-accent-foreground rounded-md px-3 py-2 text-sm font-medium transition-colors"
                                        >
                                            Send feedback
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="absolute inset-y-0 right-0 flex items-center justify-end pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                                <Menu as="div" className="relative ml-3">
                                    <div>
                                        <MenuButton className="relative flex rounded-full bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background">
                                            <span className="absolute -inset-1.5" />
                                            <span className="sr-only">Open user menu</span>
                                            <img
                                                className="h-8 w-8 rounded-full"
                                                src={`${getUserImage(user)}`}
                                                alt={user?.name}
                                            />
                                        </MenuButton>
                                    </div>
                                    {isAnonymous ? (<></>) : (
                                        <Transition
                                            enter="transition ease-out duration-100"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                        >
                                            <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-card border border-border py-1 shadow-lg focus:outline-none">
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="/profile"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Your Profile
                                                        </a>
                                                    )}
                                                </MenuItem>
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="/settings"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Settings
                                                        </a>
                                                    )}
                                                </MenuItem>
                                                {(isInstaller && user?.installer_id) ? <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href={`/installer/${user.installer_id}`}
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Installer Admin
                                                        </a>
                                                    )}
                                                </MenuItem> : <></>}
                                                {(isHacker || isActive) ? <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="https://discord.gg/sGXU582WeK"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Discord Server
                                                        </a>
                                                    )}
                                                </MenuItem> : <></>}
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="/passkey_create"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Add Passkey
                                                        </a>
                                                    )}
                                                </MenuItem>
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="#"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setShowDebugUI(!showDebugUI);
                                                            }}
                                                        >
                                                            {showDebugUI ? 'Hide' : 'Show'} Theme Debug
                                                        </a>
                                                    )}
                                                </MenuItem>
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="/login"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Sign in
                                                        </a>
                                                    )}
                                                </MenuItem>
                                            </MenuItems>
                                        </Transition>)}
                                </Menu>
                                {headerContent && (
                                    <div className="ml-4 flex items-center max-w-1/2 w-1/2 overflow-hidden">{headerContent}</div>
                                )} 
                            </div>
                        </div>
                    </div>

                    <DisclosurePanel className="sm:hidden bg-background border-t border-border">
                        <div className="space-y-1 px-2 pb-3 pt-2">
                            {navigation().map((item) => (
                                <DisclosureButton
                                    key={item.name}
                                    as="a"
                                    href={item.href}
                                    className={classNames(
                                        item.current 
                                            ? 'bg-primary text-primary-foreground' 
                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                        'block rounded-md px-3 py-2 text-base font-medium transition-colors'
                                    )}
                                    aria-current={item.current ? 'page' : undefined}
                                >
                                    {item.name}
                                </DisclosureButton>
                            ))}
                        </div>
                    </DisclosurePanel>
                    <ThemeDebug enabled={showDebugUI} />
                </>
            )}
        </Disclosure>
        <ProblemReportOverlay
          isOpen={isProblemReportOpen}
          onClose={() => setIsProblemReportOpen(false)}
          screenshot={screenshot}
          onSubmit={handleProblemReport}
        />
        </>
    )
}

export default IINavBar;